/**
 * Video Review — vertical review videos with the reviewed product attached.
 *
 * Each card ships its player markup inside an inert <template>, so no iframe or
 * <video> is fetched until the visitor actually presses play. Liquid builds that
 * markup, which keeps YouTube and Shopify-hosted videos on the same code path.
 */
(function () {
  'use strict';

  if (customElements.get('video-review')) return;

  var SCROLL_RATIO = 0.9;

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  class VideoReview extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;

      this.track = this.querySelector('[data-vr-track]');

      this.addEventListener('click', this.onClick.bind(this));

      this._refresh = function () {
        if (this._frame) return;
        this._frame = requestAnimationFrame(
          function () {
            this._frame = null;
            this.refreshArrows();
          }.bind(this)
        );
      }.bind(this);

      // Poster fallback. YouTube answers a missing still with a 120x90 grey
      // placeholder instead of a hard 404, so the image's own size is the only
      // reliable signal — `error` alone never fires. Neither load nor error
      // bubbles, hence the capturing listeners.
      var swap = this.checkPoster.bind(this);
      this.addEventListener('load', swap, true);
      this.addEventListener('error', swap, true);
      toArray(this.querySelectorAll('img[data-vr-fallback]')).forEach(function (img) {
        if (img.complete) swap({ type: 'load', target: img });
      });

      window.addEventListener('resize', this._refresh);
      if (this.track) this.track.addEventListener('scroll', this._refresh, { passive: true });

      if ('ResizeObserver' in window) {
        this._observer = new ResizeObserver(this._refresh);
        this._observer.observe(this);
      }

      this.refreshArrows();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this._refresh);
      if (this._observer) this._observer.disconnect();
    }

    /** Replace a YouTube still that came back as the grey placeholder. */
    checkPoster(event) {
      var img = event.target;
      if (!img || img.tagName !== 'IMG') return;

      var fallback = img.getAttribute('data-vr-fallback');
      if (!fallback) return;

      img.removeAttribute('data-vr-fallback');
      if (event.type === 'load' && img.naturalWidth > 120) return;
      img.src = fallback;
    }

    onClick(event) {
      var arrow = event.target.closest('[data-vr-scroll]');
      if (arrow && this.contains(arrow)) {
        this.scrollBy(parseInt(arrow.getAttribute('data-vr-scroll'), 10) || 1);
        return;
      }

      var play = event.target.closest('[data-vr-play]');
      if (play && this.contains(play)) {
        event.preventDefault();
        this.play(play.closest('[data-vr-media]'));
      }
    }

    scrollBy(direction) {
      if (!this.track) return;
      this.track.scrollBy({
        left: direction * this.track.clientWidth * SCROLL_RATIO,
        behavior: 'smooth'
      });
    }

    /** Swap the poster for the real player, and stop whatever else is playing. */
    play(media) {
      if (!media || media.classList.contains('is-playing')) return;

      var template = media.querySelector('template[data-vr-embed]');
      if (!template) return;

      this.stopAll();

      media.appendChild(template.content.cloneNode(true));
      media.classList.add('is-playing');

      var poster = media.querySelector('[data-vr-poster]');
      if (poster) poster.hidden = true;

      // A cloned <video> needs an explicit nudge; autoplay is allowed here
      // because we are inside the click that triggered it.
      var video = media.querySelector('video');
      if (video && typeof video.play === 'function') {
        var started = video.play();
        if (started && typeof started.catch === 'function') started.catch(function () {});
      }
    }

    stopAll() {
      toArray(this.querySelectorAll('[data-vr-media].is-playing')).forEach(function (media) {
        toArray(media.querySelectorAll('iframe, video')).forEach(function (node) {
          node.remove();
        });
        media.classList.remove('is-playing');
        var poster = media.querySelector('[data-vr-poster]');
        if (poster) poster.hidden = false;
      });
    }

    refreshArrows() {
      var prev = this.querySelector('[data-vr-scroll="-1"]');
      var next = this.querySelector('[data-vr-scroll="1"]');
      if (!this.track || !prev || !next) return;
      if (!this.track.clientWidth) return;

      var max = this.track.scrollWidth - this.track.clientWidth;
      var overflowing = max > 2;

      prev.hidden = !overflowing || this.track.scrollLeft <= 2;
      next.hidden = !overflowing || this.track.scrollLeft >= max - 2;
    }
  }

  customElements.define('video-review', VideoReview);

  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target.querySelector('video-review');
    if (section && typeof section.refreshArrows === 'function') section.refreshArrows();
  });

  // Leaving a section in the theme editor should not keep audio running.
  document.addEventListener('shopify:section:unload', function (event) {
    var section = event.target.querySelector('video-review');
    if (section && typeof section.stopAll === 'function') section.stopAll();
  });
})();
